export interface Organization {
  uuid: string;
  name: string;
  slug: string;
  description?: string;
  email?: string;
  phone?: string;
  website?: string;
  address?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  tax_id?: string;
  fiscal_id?: string;
  legal_name?: string;
  tax_advisor_id?: string;
  // Legal Representative
  legal_rep_first_name?: string;
  legal_rep_last_name?: string;
  legal_rep_national_id?: string;
  legal_rep_email?: string;
  logo_signed_url?: string;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

/** Compact shape returned by GET /users/me/memberships. */
export interface OrganizationSummary {
  uuid: string;
  name: string;
  slug: string;
  logo_signed_url?: string | null;
  is_active: boolean;
}

export interface UserOrganization {
  organization: OrganizationSummary;
  role: string;
  is_default: boolean;
  joined_at: string;
}

export interface OrganizationUser {
  uuid: string;
  user: {
    uuid: string;
    first_name?: string | null;
    last_name?: string | null;
    full_name?: string;
    email: string;
    avatar_signed_url?: string | null;
    profile_picture_signed_url?: string | null;
    last_login_at?: string | null;
  };
  organization: {
    uuid: string;
    name: string;
    slug: string;
  };
  role: string;
  is_default: boolean;
  is_active: boolean;
  joined_at: string;
}

export interface OrganizationTemplate {
  uuid: string;
  name: string;
  description: string | null;
  type: string;
  category: string;
  language: string;
  industry: string;
  is_system_template: boolean;
  is_active: boolean;
  version: string;
  updated_at: string | null;
}

// Request type for updating organization data
export interface UpdateOrganizationRequest {
  logo?: string | null;
  name?: string;
  fiscal_id?: string;
  tax_advisor_id?: string;
  address?: string;
  postal_code?: string;
  city?: string;
  state?: string;
  country?: string;
  email?: string;
  phone?: string;
  website?: string;
  legal_rep_first_name?: string;
  legal_rep_last_name?: string;
  legal_rep_national_id?: string;
  legal_rep_email?: string;
}

// Response type for organization API operations
export interface OrganizationResponse {
  uuid: string;
  name: string;
  slug: string;
  email?: string | null;
  phone?: string | null;
  website?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
  fiscal_id?: string | null;
  tax_advisor_id?: string | null;
  legal_rep_first_name?: string | null;
  legal_rep_last_name?: string | null;
  legal_rep_national_id?: string | null;
  legal_rep_email?: string | null;
  logo_signed_url?: string | null;
  is_active: boolean;
  created_at: string;
  updated_at?: string | null;
}

export interface OrganizationFormData {
  name: string;
  advisoryNumber: string;
  nif: string;
  email?: string;
  phone?: string;
  website?: string;
  fiscal_id?: string;
  tax_advisor_id?: string;
  address: {
    street: string;
    city: string;
    postalCode: string;
    province: string;
    country: string;
  };
  colorPalette: string;
  logo: File | null;
  logo_signed_url?: string;
}

export interface CurrentOrganizationContext {
  organization_uuid: string;
  organization_name: string;
  organization_slug: string;
  organization_logo_url: string | null;
  user_role: string;
  is_default: boolean;
}

export interface BackendOrganizationResponse {
  id?: number;
  uuid?: string;
  name: string;
  slug?: string;
  email?: string;
  phone?: string;
  website?: string;
  address?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  fiscal_id?: string;
  tax_id?: string;
  legal_name?: string;
  legal_rep_first_name?: string;
  legal_rep_last_name?: string;
  legal_rep_national_id?: string;
  legal_rep_email?: string;
  logo_signed_url?: string;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
  tax_advisor_id?: string;
}

const OWNER_ROLE = "owner";

export function canEditOrganization(role: string | null | undefined): boolean {
  return role?.toLowerCase() === OWNER_ROLE;
}

export const INTERNAL_MEMBER_ROLES = ["owner", "employee"] as const;

export type InternalMemberRole = (typeof INTERNAL_MEMBER_ROLES)[number];

export function isInternalMemberRole(role: string): role is InternalMemberRole {
  return (INTERNAL_MEMBER_ROLES as readonly string[]).includes(role);
}

/** The organization always keeps one active owner: the server refuses the rest. */
export function isLastActiveOwner(
  member: { uuid: string; role: string; is_active: boolean },
  members: ReadonlyArray<{ uuid: string; role: string; is_active: boolean }>,
): boolean {
  if (member.role !== OWNER_ROLE || !member.is_active) return false;
  return (
    members.filter((candidate) => candidate.role === OWNER_ROLE && candidate.is_active).length <= 1
  );
}

export function canManageMembers(
  currentUserRole: string | null | undefined,
  members: ReadonlyArray<Pick<OrganizationUser, "is_default" | "role">>,
): boolean {
  const defaultMemberRole = members.find((member) => member.is_default)?.role;
  return currentUserRole?.toLowerCase() === OWNER_ROLE || defaultMemberRole === OWNER_ROLE;
}
